/* Master · Medicine requests — pieces shared by the two screens in this flow:
     MedicineRequests.jsx          the requester's side (raise + track)
     MedicineRequestApprovals.jsx  the master-data team's side (approve / reject)

   Both talk to v1/medicine-requests via supplyService, list the same rows and
   show the same read-only detail, so the query hook, the table, the pager, the
   drawer shell and the form chrome live here. */
import React, { useEffect, useState } from "react";
import supplyService from "../../Services/supplyService";
import { cbFmtINR, cbFmtNum, cbFmtDateTime } from "../Supply/supplyData";
import {
  Button,
  Pill,
  Select,
  Table,
  Tr,
  Td,
  ToolbarBar,
  KV,
  Eyebrow,
  IconChevRight,
  IconX,
} from "../Supply/supplyKit";

/* PENDING is the only state a request can be decided from. */
export const STATUSES = ["PENDING", "APPROVED", "REJECTED"];

const STATUS_VARIANT = { PENDING: "amber", APPROVED: "green", REJECTED: "red" };

export const StatusPill = ({ status }) => (
  <Pill variant={STATUS_VARIANT[status] || "neutral"} dot>
    {String(status || "").toLowerCase() || "—"}
  </Pill>
);

/* "—" instead of a blank cell for the many optional fields on a request. */
export const dash = (v) => (v === null || v === undefined || v === "" ? "—" : v);
export const money = (v) => (v === null || v === undefined || v === "" ? "—" : cbFmtINR(v));
export const when = (iso) => cbFmtDateTime(iso) || "—";

/* ---------------- Form field chrome (matches the Doctors / Suppliers drawers) ---------------- */
export const fieldInput = {
  width: "100%",
  height: 30,
  padding: "0 8px",
  border: "1px solid var(--border-default)",
  borderRadius: 4,
  fontSize: 12,
  fontFamily: "var(--font-sans)",
  background: "var(--bg-panel)",
  color: "var(--fg-default)",
  outline: "none",
};

const fieldLabel = {
  display: "block",
  fontSize: 10,
  fontWeight: 600,
  letterSpacing: "0.04em",
  textTransform: "uppercase",
  color: "var(--fg-muted)",
  marginBottom: 4,
};

export const Field = ({ label, hint, wide, children }) => (
  <div style={wide ? { gridColumn: "1 / -1" } : undefined}>
    <label style={fieldLabel}>{label}</label>
    {children}
    {hint && <div style={{ fontSize: 10, color: "var(--fg-muted)", marginTop: 4 }}>{hint}</div>}
  </div>
);

/* ---------------- List query ----------------
   One hook for both screens. `status` "" means every status. A changed filter
   invalidates the page number, so it is reset during render (rather than in an
   effect) to avoid firing a throwaway request for a page that no longer exists. */
export const useMedicineRequests = ({ status = "", search = "", warehouseId = "" } = {}) => {
  const [size, setSize] = useState(20);
  const key = `${status}|${search}|${warehouseId}|${size}`;
  const [state, setState] = useState(() => ({ key, page: 0 }));
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ totalPages: 0, totalElements: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  if (state.key !== key) setState({ key, page: 0 });
  const page = state.key === key ? state.page : 0;

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    supplyService
      .getMedicineRequests({ status: status || undefined, search, warehouseId, page, size })
      .then((res) => {
        if (!alive) return;
        setRows(res.content || []);
        setMeta({ totalPages: res.totalPages || 0, totalElements: res.totalElements || 0 });
        setLoading(false);
      })
      .catch((e) => {
        if (!alive) return;
        setRows([]);
        setMeta({ totalPages: 0, totalElements: 0 });
        setError(e?.response?.data?.message || e?.message || "Could not load medicine requests.");
        setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [status, search, warehouseId, page, size, reloadKey]);

  return {
    rows,
    loading,
    error,
    page,
    size,
    setSize,
    setPage: (p) => setState({ key, page: Math.max(0, p) }),
    totalPages: meta.totalPages,
    totalElements: meta.totalElements,
    reload: () => setReloadKey((k) => k + 1),
  };
};

/* Per-status totals for the tab chips. Each call asks for a single row and reads
   only totalElements, so the three run cheaply alongside the list query. */
export const useMedicineRequestCounts = ({ search = "", warehouseId = "", reloadKey = 0 } = {}) => {
  const [counts, setCounts] = useState({});

  useEffect(() => {
    let alive = true;
    Promise.all(
      STATUSES.map((status) =>
        supplyService
          .getMedicineRequests({ status, search, warehouseId, page: 0, size: 1 })
          .then((res) => res.totalElements || 0)
          .catch(() => null)
      )
    ).then((totals) => {
      if (!alive) return;
      setCounts(STATUSES.reduce((acc, s, i) => ({ ...acc, [s]: totals[i] }), {}));
    });
    return () => {
      alive = false;
    };
  }, [search, warehouseId, reloadKey]);

  return counts;
};

/* Debounced mirror of a text input, so typing in the search box doesn't fire a
   request per keystroke. */
export const useDebounced = (value, delay = 350) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
};

/* ---------------- List table ---------------- */
export const RequestsTable = ({ rows, onOpen, emptyHint }) => (
  <Table
    columns={[
      { label: "Request #" },
      { label: "Medicine" },
      { label: "Supplier / invoice" },
      { label: "Warehouse" },
      { label: "Requested by" },
      { label: "Raised" },
      { label: "Status" },
      { label: "", width: 24 },
    ]}
  >
    {rows.map((r) => (
      <Tr key={r.id ?? r.requestNumber} onClick={() => onOpen(r)}>
        <Td mono style={{ fontWeight: 600, color: "var(--fg-strong)" }}>{dash(r.requestNumber)}</Td>
        <Td>
          <div style={{ fontWeight: 600, color: "var(--fg-strong)" }}>{dash(r.medicineName)}</div>
          {(r.manufacturer || r.packing) && (
            <div style={{ fontSize: 10, color: "var(--fg-muted)" }}>
              {[r.manufacturer, r.packing].filter(Boolean).join(" · ")}
            </div>
          )}
        </Td>
        <Td>
          <div>{dash(r.supplierName)}</div>
          {r.invoiceNumber && (
            <div style={{ fontSize: 10, color: "var(--fg-muted)", fontFamily: "var(--font-mono)" }}>
              {r.invoiceNumber}
            </div>
          )}
        </Td>
        <Td mono>{dash(r.warehouseId)}</Td>
        <Td>
          <div>{dash(r.requesterName || r.requestedBy)}</div>
          {r.requesterNumber && (
            <div style={{ fontSize: 10, color: "var(--fg-muted)", fontFamily: "var(--font-mono)" }}>
              {r.requesterNumber}
            </div>
          )}
        </Td>
        <Td mono style={{ whiteSpace: "nowrap" }}>{when(r.requestedAt)}</Td>
        <Td>
          <StatusPill status={r.status} />
        </Td>
        <Td>
          <IconChevRight size={12} style={{ color: "var(--fg-muted)" }} />
        </Td>
      </Tr>
    ))}
    {rows.length === 0 && (
      <Tr>
        <Td colSpan={8} style={{ textAlign: "center", color: "var(--fg-muted)", padding: 20 }}>
          {emptyHint}
        </Td>
      </Tr>
    )}
  </Table>
);

/* ---------------- Pager ---------------- */
export const RequestPager = ({ q }) => {
  const start = q.totalElements === 0 ? 0 : q.page * q.size + 1;
  const end = Math.min(q.totalElements, (q.page + 1) * q.size);
  return (
    <ToolbarBar>
      <Select value={q.size} onChange={(e) => q.setSize(Number(e.target.value))}>
        {[20, 50, 100].map((n) => (
          <option key={n} value={n}>
            {n} / page
          </option>
        ))}
      </Select>
      <div style={{ marginLeft: "auto", display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8 }}>
        <span style={{ fontSize: 11, color: "var(--fg-muted)", fontFamily: "var(--font-mono)" }}>
          {start}–{end} of {cbFmtNum(q.totalElements)}
        </span>
        <Button size="sm" disabled={q.page <= 0 || q.loading} onClick={() => q.setPage(q.page - 1)}>
          ← Prev
        </Button>
        <Button
          size="sm"
          disabled={q.page + 1 >= q.totalPages || q.loading}
          onClick={() => q.setPage(q.page + 1)}
        >
          Next →
        </Button>
      </div>
    </ToolbarBar>
  );
};

/* ---------------- Drawer shell ---------------- */
export const Drawer = ({ icon, title, sub, onClose, footer, children, width = 560 }) => (
  <div style={{ position: "fixed", inset: 0, zIndex: 70 }}>
    <div onClick={onClose} style={{ position: "absolute", inset: 0, background: "var(--bg-overlay)" }} />
    <div
      className="cb-animate-in"
      style={{
        position: "absolute",
        top: 0,
        right: 0,
        bottom: 0,
        width: `min(${width}px, 96vw)`,
        background: "var(--bg-canvas)",
        boxShadow: "-12px 0 40px rgba(13,17,27,0.18)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          padding: "12px 20px",
          borderBottom: "1px solid var(--border-default)",
          background: "var(--bg-panel)",
        }}
      >
        {icon && (
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: 6,
              background: "var(--ink-50)",
              color: "var(--ink-700)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            {icon}
          </div>
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: "var(--fg-strong)" }}>{title}</div>
          {sub && <div style={{ fontSize: 11, color: "var(--fg-muted)", marginTop: 2 }}>{sub}</div>}
        </div>
        <button
          onClick={onClose}
          style={{
            border: 0,
            background: "transparent",
            cursor: "pointer",
            color: "var(--fg-muted)",
            width: 28,
            height: 28,
            borderRadius: 4,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <IconX size={16} />
        </button>
      </div>

      <div style={{ flex: 1, overflow: "auto", padding: "16px 20px" }} className="cb-scroll">
        {children}
      </div>

      {footer && (
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 8,
            alignItems: "center",
            padding: "12px 20px",
            borderTop: "1px solid var(--border-default)",
            background: "var(--bg-panel)",
          }}
        >
          {footer}
        </div>
      )}
    </div>
  </div>
);

/* ---------------- Read-only detail ----------------
   The same grid on both screens: everything the requester submitted, then the
   decision block once the request has left PENDING. */
const Group = ({ title, children, cols = 2 }) => (
  <div style={{ marginBottom: 18 }}>
    <Eyebrow style={{ display: "block", marginBottom: 8 }}>{title}</Eyebrow>
    <div style={{ display: "grid", gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`, gap: 12 }}>
      {children}
    </div>
  </div>
);

export const RequestDetail = ({ req }) => (
  <div>
    <Group title="Medicine">
      <KV label="Medicine name" value={dash(req.medicineName)} />
      <KV label="Manufacturer" value={dash(req.manufacturer)} />
      <KV label="Composition" value={dash(req.composition)} />
      <KV label="Packing" value={dash(req.packing)} />
      <KV label="Units / pack" value={dash(req.unitPack)} mono />
      <KV label="HSN code" value={dash(req.hsnCode)} mono />
    </Group>

    <Group title="Commercials">
      <KV label="MRP" value={money(req.mrp)} mono />
      <KV label="Purchase price" value={money(req.purchasePrice)} mono />
      <KV label="GST %" value={req.gstPercentage === null || req.gstPercentage === "" ? "—" : `${req.gstPercentage}%`} mono />
    </Group>

    <Group title="Source">
      <KV label="Supplier" value={dash(req.supplierName)} />
      <KV label="Supplier id" value={dash(req.supplierId)} mono />
      <KV label="Invoice number" value={dash(req.invoiceNumber)} mono />
      <KV label="Invoice date" value={dash(req.invoiceDate)} mono />
      <KV label="Warehouse" value={dash(req.warehouseId)} mono />
    </Group>

    <Group title="Requester">
      <KV label="Name" value={dash(req.requesterName)} />
      <KV label="Mobile" value={dash(req.requesterNumber)} mono />
      <KV label="Raised by" value={dash(req.requestedBy)} />
      <KV label="Raised on" value={when(req.requestedAt)} mono />
      <div style={{ gridColumn: "1 / -1" }}>
        <KV label="Remarks" value={dash(req.remarks)} small />
      </div>
    </Group>

    {req.status !== "PENDING" && (
      <Group title={req.status === "APPROVED" ? "Approval" : "Rejection"}>
        <KV label="Reviewed by" value={dash(req.reviewedBy)} />
        <KV label="Reviewed on" value={when(req.reviewedAt)} mono />
        {req.status === "APPROVED" && <KV label="Medicine code" value={dash(req.medicineCode)} mono />}
        <div style={{ gridColumn: "1 / -1" }}>
          {/* One backend column: the approver's note, or the rejection reason. */}
          <KV
            label={req.status === "APPROVED" ? "Remarks" : "Reason"}
            value={dash(req.reviewRemarks)}
            small
          />
        </div>
      </Group>
    )}
  </div>
);

/* A decided request cannot be decided again — the backend refuses anything that
   isn't PENDING with a 422 naming the reviewer who got there first. That message
   is worth showing verbatim; it just needs the "so refresh" half. */
export const decisionError = (e) => {
  const msg = e?.response?.data?.message || e?.message || "";
  if (e?.response?.status === 422 || /already/i.test(msg)) {
    return `${msg || "This request has already been decided."} Refresh the queue to see the current status.`;
  }
  return msg || "The decision could not be saved.";
};
